import React, { createContext, useState, useEffect, useRef } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';
import {
  useWorkspaces,
  resolveCurrentWorkspace,
  getSelectedWorkspaceId,
  setSelectedWorkspaceId as persistWorkspaceId,
  type Workspace,
} from '@/hooks/useWorkspaces';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';

export interface WorkspaceContextType {
  selectedWorkspaceId: string | null;
  selectedWorkspace: Workspace | null;
  setSelectedWorkspaceId: (id: string | null) => void;
  workspaces: Workspace[] | null;
  isLoading: boolean;
}

const globalKey = '__WORKSPACE_CONTEXT__';
const globalObject = typeof globalThis !== 'undefined' ? (globalThis as any) : {};

export const WorkspaceContext: React.Context<WorkspaceContextType | undefined> =
  globalObject[globalKey] || createContext<WorkspaceContextType | undefined>(undefined);

if (globalObject && !globalObject[globalKey]) {
  globalObject[globalKey] = WorkspaceContext;
}

// Org-level queries whose data does not change with the selected workspace.
const WORKSPACE_INDEPENDENT_ROOTS = new Set(['current-subscription', 'workspaces-v1']);

/*
  On a workspace switch every workspace-scoped query is invalidated, as before, so a
  workspace you return to refetches instead of showing what it had when you left.
  Only the org-level roots above are kept.
*/
function shouldInvalidateOnWorkspaceSwitch(query: Pick<Query, 'queryKey'>): boolean {
  const root = query.queryKey[0];
  return !(typeof root === 'string' && WORKSPACE_INDEPENDENT_ROOTS.has(root));
}

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: workspaces, isLoading } = useWorkspaces();

  const [selectedWorkspaceId, setSelectedWorkspaceIdState] = useState<string | null>(
    () => getSelectedWorkspaceId(),
  );

  const setSelectedWorkspaceId = (id: string | null) => {
    persistWorkspaceId(id);
    setSelectedWorkspaceIdState(id);
  };

  const selectedWorkspace = resolveCurrentWorkspace(workspaces, selectedWorkspaceId);
  const resolvedId = selectedWorkspace?.id ?? null;

  useEffect(() => {
    if (resolvedId && resolvedId !== selectedWorkspaceId) {
      persistWorkspaceId(resolvedId);
      setSelectedWorkspaceIdState(resolvedId);
    }
  }, [resolvedId, selectedWorkspaceId]);

  const { setWorkspaceAccountOverride } = useBusinessAccountContext();
  const bridgeBusinessAccountId = selectedWorkspace?.business_account_id ?? null;
  useEffect(() => {
    if (!workspaces) return; // wait for the list so we don't flash empty on load
    setWorkspaceAccountOverride(bridgeBusinessAccountId);
  }, [workspaces, bridgeBusinessAccountId, setWorkspaceAccountOverride]);

  const queryClient = useQueryClient();
  const prevWorkspaceIdRef = useRef<string | null>(resolvedId);
  const hasResolvedOnceRef = useRef(false);
  const persistedIdAtMountRef = useRef<string | null>(getSelectedWorkspaceId());
  useEffect(() => {
    if (prevWorkspaceIdRef.current === resolvedId) return;
    prevWorkspaceIdRef.current = resolvedId;

    // The first resolution after a page load is not a workspace switch. The ref
    // above starts as `null` because the list has not arrived, so when it does
    // this fired and invalidated every query in the cache on every reload.
    // Unless the list resolved to a different workspace than the persisted one
    // the requests used, nothing was fetched under the wrong scope.
    if (!hasResolvedOnceRef.current) {
      hasResolvedOnceRef.current = true;
      if (resolvedId === persistedIdAtMountRef.current) return;
    }

    queryClient.invalidateQueries({ predicate: shouldInvalidateOnWorkspaceSwitch });
  }, [resolvedId, queryClient]);

  return (
    <WorkspaceContext.Provider
      value={{
        selectedWorkspaceId: resolvedId,
        selectedWorkspace,
        setSelectedWorkspaceId,
        workspaces: workspaces ?? null,
        isLoading,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export function useWorkspaceContext(): WorkspaceContextType {
  const ctx = React.useContext(WorkspaceContext);
  if (!ctx) {
    return {
      selectedWorkspaceId: null,
      selectedWorkspace: null,
      setSelectedWorkspaceId: () => {},
      workspaces: null,
      isLoading: false,
    };
  }
  return ctx;
}
